import { FilterOutlined } from "@ant-design/icons"
import { Badge, Button, Drawer, Flex, Select, Typography } from "antd"
import React, { useMemo } from "react"

import { type AttachmentFilters } from "../../../../../../Album/redux/album.types.ts"
import { type AttachmentFilterOptions } from "../../../../../../Album/types/attachment.types.ts"

const { Text } = Typography

interface ProjectAllFilesFilterDrawerProps {
  open: boolean
  onClose: () => void
  filters: AttachmentFilters
  options: AttachmentFilterOptions | null
  onFilterChange: (data: Partial<AttachmentFilters>) => void
}

export function ProjectAllFilesFilterDrawer({
  open,
  onClose,
  filters,
  options,
  onFilterChange
}: ProjectAllFilesFilterDrawerProps) {
  const handleFilterChange =
    (key: keyof AttachmentFilters) => (value: string | undefined) => {
      onFilterChange({ [key]: value?.length ? value : null })
    }

  const handleClearAll = () => {
    onFilterChange({
      milestone: null,
      invoice: null,
      checklist_item: null,
      financial_item: null
    })
  }

  const activeFiltersCount = useMemo(() => {
    let count = 0
    if (filters?.milestone?.length) count++
    if (filters?.invoice?.length) count++
    if (filters?.checklist_item?.length) count++
    if (filters?.financial_item?.length) count++
    return count
  }, [filters])

  const filterSections = [
    {
      key: "milestone" as const,
      label: "Milestones",
      options: options?.milestones || [],
      value: filters?.milestone,
      visible: !filters?.model_type || filters.model_type === "milestone"
    },
    {
      key: "invoice" as const,
      label: "Invoices",
      options: options?.invoices || [],
      value: filters?.invoice,
      visible: !filters?.model_type || filters.model_type === "invoice"
    },
    {
      key: "checklist_item" as const,
      label: "Checklist Items",
      options: options?.checklist_items || [],
      value: filters?.checklist_item,
      visible: !filters?.model_type || filters.model_type === "checklist_item"
    },
    {
      key: "financial_item" as const,
      label: "Financial Items",
      options: options?.financial_items || [],
      value: filters?.financial_item,
      visible: !filters?.model_type || filters.model_type === "financial_item"
    }
  ]

  const visibleSections = filterSections.filter((section) => section.visible)

  return (
    <Drawer
      title="Advanced Filters"
      placement="right"
      width={360}
      onClose={onClose}
      open={open}
      extra={
        activeFiltersCount > 0 && (
          <Button type="link" onClick={handleClearAll} size="small">
            Clear All
          </Button>
        )
      }
    >
      <Flex vertical gap="large">
        {visibleSections.length === 0 ? (
          <Text type="secondary">
            Select a &quot;Related To&quot; filter to see available options.
          </Text>
        ) : (
          visibleSections.map((section) => (
            <Flex vertical gap={8} key={section.key}>
              <Text strong>{section.label}</Text>
              <Select<string>
                // mode="multiple"
                allowClear
                placeholder={`Select ${section.label.toLowerCase()}`}
                value={section.value || ""}
                onChange={handleFilterChange(section.key)}
                options={section.options}
                style={{ width: "100%" }}
                // maxTagCount="responsive"
                // showSearch
                filterOption={(input, option) =>
                  (option?.label as string)
                    ?.toLowerCase()
                    .includes(input.toLowerCase())
                }
                notFoundContent={
                  <Text type="secondary">
                    No {section.label.toLowerCase()} found
                  </Text>
                }
              />
            </Flex>
          ))
        )}
      </Flex>
    </Drawer>
  )
}

interface FilterDrawerTriggerProps {
  onClick: () => void
  activeCount: number
}

export function FilterDrawerTrigger({
  onClick,
  activeCount
}: FilterDrawerTriggerProps) {
  return (
    <Badge count={activeCount} size="small">
      <Button icon={<FilterOutlined />} onClick={onClick}>
        More Filters
      </Button>
    </Badge>
  )
}

export function useFilterDrawerCount(filters: AttachmentFilters): number {
  return useMemo(() => {
    let count = 0
    if (filters?.milestone?.length) count++
    if (filters?.invoice?.length) count++
    if (filters?.checklist_item?.length) count++
    if (filters?.financial_item?.length) count++
    return count
  }, [filters])
}
